import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { GoogleCalendarDatePicker } from './GoogleCalendarDatePicker';
import {
  Search, Download, Play, Eye, Flame, UserX, Car, Clock, Camera,
  AlertTriangle, ArrowUpDown, MoreVertical, Share, Trash2, Info,
  Calendar, MapPin, Activity, Shield, Target, Zap, ChevronLeft,
  ChevronRight, Maximize2, Layers, BarChart3, History as HistoryIcon,
  Filter, X
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ImageWithFallback } from './figma/ImageWithFallback';

// --- Shared Glass Components ---

const GlassPanel: React.FC<{ children: React.ReactNode; className?: string; title?: string; icon?: any; headerAction?: React.ReactNode }> = ({ children, className = '', title, icon: Icon, headerAction }) => (
  <div className={`bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl overflow-hidden transition-all duration-300 shadow-2xl ${className}`}>
    {(title || Icon || headerAction) && (
      <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between bg-white/[0.02]">
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-blue-400" />}
          {title && <h3 className="text-sm font-bold tracking-tight text-blue-100/90 uppercase pr-4">{title}</h3>}
        </div>
        {headerAction}
      </div>
    )}
    <div className="p-6">
      {children}
    </div>
  </div>
);

const StatCard: React.FC<{ label: string; value: string; icon: any; color: string; bgColor: string }> = ({ label, value, icon: Icon, color, bgColor }) => (
  <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 relative overflow-hidden group hover:bg-white/10 transition-all">
    <div className={`absolute top-0 right-0 w-24 h-24 ${bgColor} blur-[40px] opacity-20 -mr-12 -mt-12 group-hover:scale-150 transition-transform duration-700`} />
    <div className="flex items-center gap-4 relative z-10">
      <div className={`w-12 h-12 rounded-xl ${bgColor} border border-white/10 flex items-center justify-center`}>
        <Icon className={`w-6 h-6 ${color}`} />
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-widest text-blue-100/40 font-bold mb-1">{label}</p>
        <h4 className="text-2xl font-black text-white">{value}</h4>
      </div>
    </div>
  </div>
);

// --- Sub-Components ---

interface DetectionEvent {
  id: string;
  type: 'motion' | 'fire' | 'fall' | 'anpr';
  camera: string;
  location: string;
  timestamp: Date;
  duration: number;
  severity: 'low' | 'medium' | 'high';
  description: string;
  hasVideo: boolean;
  confidence: number;
  imageUrl?: string;
  additionalData?: any;
}

const mapAlertTypeToEventType = (rawType: string, usecase: string): DetectionEvent['type'] => {
  const t = `${rawType} ${usecase}`.toLowerCase();
  if (t.includes('fire') || t.includes('smoke')) return 'fire';
  if (t.includes('fall')) return 'fall';
  if (t.includes('plate') || t.includes('anpr')) return 'anpr';
  return 'motion';
};

const mapPriorityToSeverity = (priority: string): DetectionEvent['severity'] => {
  const p = (priority || '').toLowerCase();
  if (p === 'critical' || p === 'high') return 'high';
  if (p === 'medium') return 'medium';
  return 'low';
};

const EventCard: React.FC<{ event: DetectionEvent; onClick: () => void }> = ({ event, onClick }) => {
  const getEventStyles = (type: string) => {
    switch (type) {
      case 'fire': return { icon: Flame, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20' };
      case 'fall': return { icon: UserX, color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/20' };
      case 'anpr': return { icon: Car, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' };
      default: return { icon: Eye, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20' };
    }
  };

  const styles = getEventStyles(event.type);
  const Icon = styles.icon;

  return (
    <motion.div 
      whileHover={{ y: -2 }}
      onClick={onClick}
      className="group relative bg-white/5 border border-white/10 rounded-2xl p-5 cursor-pointer hover:bg-white/[0.08] hover:border-blue-500/30 transition-all shadow-xl overflow-hidden"
    >
      <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500 blur-[60px] opacity-[0.02] group-hover:opacity-[0.05] transition-opacity" />
      
      <div className="flex flex-col md:flex-row gap-5">
        {/* Visual Proxy */}
        <div className="w-full md:w-40 aspect-video rounded-xl bg-black/40 border border-white/5 relative overflow-hidden flex-shrink-0">
           {event.imageUrl ? (
             <ImageWithFallback
               src={event.imageUrl}
               alt="Event Proof"
               className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity grayscale-[0.3] group-hover:grayscale-0"
             />
           ) : (
             <div className="w-full h-full flex items-center justify-center">
                <Camera className="w-6 h-6 text-blue-100/10" />
             </div>
           )}
           {event.hasVideo && (
             <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-8 h-8 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center">
                   <Play className="w-3.5 h-3.5 text-white fill-white" />
                </div>
             </div>
           )}
           <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/60 backdrop-blur-md rounded-md text-[8px] font-black text-white uppercase border border-white/10">
              {event.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
           </div>
        </div>

        <div className="flex-1 space-y-3">
           <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                 <div className={`w-7 h-7 rounded-lg ${styles.bg} border ${styles.border} flex items-center justify-center`}>
                    <Icon className={`w-3.5 h-3.5 ${styles.color}`} />
                 </div>
                 <h4 className="text-xs font-black text-white uppercase tracking-tight truncate">{event.description}</h4>
              </div>
              <Badge variant="outline" className={`text-[9px] font-black uppercase tracking-tighter px-2 py-0 border-white/10 ${event.severity === 'high' ? 'bg-red-500/20 text-red-400' : 'bg-white/5 text-blue-100/40'}`}>
                 {event.severity}
              </Badge>
           </div>

           <div className="flex flex-wrap items-center gap-x-6 gap-y-2 opacity-60 group-hover:opacity-100 transition-opacity">
              <div className="flex items-center gap-1.5">
                 <Target className="w-3 h-3 text-blue-400/60" />
                 <span className="text-[10px] font-bold text-blue-100/60 uppercase">{event.camera}</span>
              </div>
              <div className="flex items-center gap-1.5">
                 <MapPin className="w-3 h-3 text-blue-400/60" />
                 <span className="text-[10px] font-bold text-blue-100/60 uppercase">{event.location}</span>
              </div>
              <div className="flex items-center gap-1.5">
                 <Zap className="w-3 h-3 text-blue-400/60" />
                 <span className="text-[10px] font-bold text-blue-100/60 uppercase">{event.confidence}% Intel</span>
              </div>
           </div>
        </div>
      </div>
    </motion.div>
  );
};

// --- Main History Component ---

export function History({ onAddAlert }: any) {
  const [activeTab, setActiveTab] = useState('log');
  const [events, setEvents] = useState<DetectionEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<DetectionEvent | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');

  useEffect(() => {
    const fetchHistory = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('csense_auth_token');
        const response = await fetch('/api/alerts?limit=200&page=1', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          const mapped: DetectionEvent[] = (data.alerts || []).map((a: any) => {
            const confidenceRaw = parseFloat(a.confidence) || 0;
            return {
              id: a.id,
              type: mapAlertTypeToEventType(a.type, a.usecase),
              camera: a.cameraName || a.camera_name || 'Unknown Camera',
              location: a.location || 'Unknown',
              timestamp: a.timestamp ? new Date(a.timestamp) : new Date(),
              duration: 0,
              severity: mapPriorityToSeverity(a.priority),
              description: a.message || (a.type ? a.type.replace(/_/g, ' ').toUpperCase() : 'DETECTION EVENT'),
              hasVideo: !!a.has_image,
              confidence: confidenceRaw <= 1 ? Math.round(confidenceRaw * 100) : Math.round(confidenceRaw),
              imageUrl: a.image_url || undefined
            };
          });
          setEvents(mapped);
        }
      } catch (e) {
        console.error('Failed to load detection history', e);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, []);

  const filteredEvents = events.filter(e => {
    const matchesSearch = e.description.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          e.camera.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || e.type === filterType;
    const matchesSeverity = filterSeverity === 'all' || e.severity === filterSeverity;
    return matchesSearch && matchesType && matchesSeverity;
  });

  const visualEvidencePct = events.length ? Math.round((events.filter(e => e.hasVideo).length / events.length) * 100) : 0;
  const anomalyCount = events.filter(e => e.severity === 'high').length;
  const avgConfidence = events.length ? Math.round(events.reduce((sum, e) => sum + e.confidence, 0) / events.length) : 0;

  const hourlyBuckets = Array.from({ length: 12 }).map((_, i) => {
    const hoursAgo = 11 - i;
    const bucketStart = new Date(Date.now() - (hoursAgo + 1) * 3600000);
    const bucketEnd = new Date(Date.now() - hoursAgo * 3600000);
    const count = events.filter(e => e.timestamp >= bucketStart && e.timestamp < bucketEnd).length;
    return { label: bucketEnd.toLocaleTimeString([], { hour: '2-digit' }), count };
  });
  const maxHourlyCount = Math.max(1, ...hourlyBuckets.map(b => b.count));

  const locationCounts = events.reduce((acc: Record<string, number>, e) => {
    acc[e.location] = (acc[e.location] || 0) + 1;
    return acc;
  }, {});
  const topLocations = Object.entries(locationCounts).sort((a, b) => b[1] - a[1]).slice(0, 4);
  const maxLocationCount = Math.max(1, ...topLocations.map(([, count]) => count));

  const typeCounts = events.reduce((acc: Record<string, number>, e) => {
    acc[e.type] = (acc[e.type] || 0) + 1;
    return acc;
  }, {});
  const typeLabels: Record<string, { label: string; icon: any; color: string }> = {
    motion: { label: 'AI-Motion', icon: Eye, color: 'text-blue-400' },
    fire: { label: 'Fire Recon', icon: Flame, color: 'text-red-400' },
    anpr: { label: 'Recon ANPR', icon: Car, color: 'text-emerald-400' },
    fall: { label: 'Fall Logic', icon: UserX, color: 'text-orange-400' }
  };

  const exportArchive = () => {
    const header = ['Timestamp', 'Type', 'Camera', 'Location', 'Severity', 'Confidence', 'Description'];
    const rows = filteredEvents.map(e => [
      e.timestamp.toISOString(), e.type, e.camera, e.location, e.severity, `${e.confidence}%`, e.description.replace(/,/g, ';')
    ]);
    const csv = [header, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `detection_chronicle_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (selectedEvent) {
    return (
      <div className="min-h-screen bg-[#0a0b14] text-white p-6 space-y-8 pb-32">
        <Button 
          variant="ghost" 
          onClick={() => setSelectedEvent(null)}
          className="text-blue-100/40 hover:text-white hover:bg-white/5 rounded-2xl px-6"
        >
          <ChevronLeft className="w-4 h-4 mr-2" /> Back to Chronicle
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
             <GlassPanel className="p-0 overflow-hidden boder-white/20">
                <div className="aspect-video bg-black relative group">
                   {selectedEvent.imageUrl ? (
                     <img
                       src={selectedEvent.imageUrl}
                       alt="Event Evidence"
                       className="w-full h-full object-cover"
                     />
                   ) : (
                     <div className="w-full h-full flex items-center justify-center">
                        <Camera className="w-12 h-12 text-blue-100/10" />
                     </div>
                   )}
                   <div className="absolute bottom-4 right-4 flex gap-2">
                       <div className="px-3 py-1.5 bg-black/60 backdrop-blur-md border border-white/10 rounded-xl flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-white">
                          <Activity className="w-3.5 h-3.5 text-blue-500" />
                          {selectedEvent.imageUrl ? 'Snapshot Evidence' : 'No Snapshot Available'}
                       </div>
                   </div>
                </div>
                <div className="p-6 bg-white/[0.02] border-t border-white/5 flex items-center justify-between">
                   <span className="text-[10px] font-mono text-blue-100/40 uppercase">{selectedEvent.timestamp.toLocaleString()}</span>
                   <div className="flex gap-2">
                      {selectedEvent.imageUrl && (
                        <a href={selectedEvent.imageUrl} download target="_blank" rel="noreferrer">
                          <Button variant="ghost" size="icon" className="text-white hover:bg-white/5"><Download className="w-4 h-4" /></Button>
                        </a>
                      )}
                   </div>
                </div>
             </GlassPanel>

             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <GlassPanel title="Event Intelligence" icon={Target}>
                   <div className="space-y-4">
                      {[
                        { l: 'Timestamp', v: selectedEvent.timestamp.toLocaleString(), i: Clock },
                        { l: 'Auth Node', v: selectedEvent.camera, i: Camera },
                        { l: 'Sector UID', v: selectedEvent.location, i: MapPin },
                        { l: 'Neural Confidence', v: `${selectedEvent.confidence}%`, i: Zap }
                      ].map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between border-b border-white/5 pb-3 last:border-0 last:pb-0">
                           <div className="flex items-center gap-2">
                              <item.i className="w-3.5 h-3.5 text-blue-400/40" />
                              <span className="text-[10px] font-black text-blue-100/20 uppercase tracking-widest">{item.l}</span>
                           </div>
                           <span className="text-xs font-bold text-white uppercase">{item.v}</span>
                        </div>
                      ))}
                   </div>
                </GlassPanel>

                <GlassPanel title="Protocol Triage" icon={Shield}>
                   <div className="space-y-4">
                      <div className="p-4 bg-white/[0.03] rounded-xl border border-white/10">
                         <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-2">Internal Log</p>
                         <p className="text-xs text-blue-100/60 leading-relaxed font-light">"{selectedEvent.description}"</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Badge className="bg-red-500/20 text-red-400 border-red-500/20 text-[9px] font-black uppercase px-3">Severity: {selectedEvent.severity}</Badge>
                        <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/20 text-[9px] font-black uppercase px-3">System: AI Recon</Badge>
                      </div>
                   </div>
                </GlassPanel>
             </div>
          </div>

          <div className="space-y-6">
             <GlassPanel title="Tactical Actions" icon={Activity}>
                <div className="space-y-3">
                   <Button className="w-full bg-blue-600 hover:bg-blue-500 text-white font-black py-6 rounded-2xl shadow-xl transition-all hover:scale-[1.02]">
                      <Download className="w-4 h-4 mr-2" /> Download Evidence
                   </Button>
                   <Button variant="outline" className="w-full border-white/10 text-white/80 hover:bg-white/5 font-black py-6 rounded-2xl">
                      <Share className="w-4 h-4 mr-2" /> Share Transmission
                   </Button>
                   <div className="pt-4 mt-4 border-t border-white/5 space-y-3">
                      <Button variant="ghost" className="w-full text-red-500/60 hover:text-red-500 hover:bg-red-500/5 font-black py-6 rounded-2xl">
                         <Trash2 className="w-4 h-4 mr-2" /> Purge from Matrix
                      </Button>
                   </div>
                </div>
             </GlassPanel>

             <GlassPanel title="Neural Notes" icon={Info}>
                <p className="text-xs text-blue-100/40 italic font-light leading-relaxed"> No manual annotations present for this entry. Neural engine reports optimal synchronization with sector topology.</p>
             </GlassPanel>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0b14] text-white p-6 space-y-8 pb-32">
      {/* Background Atmosphere */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-emerald-500/5 blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-500/5 blur-[100px]" />
      </div>

      <div className="relative z-10 space-y-8">
        <div className="flex flex-wrap items-center justify-between gap-6">
           <div>
              <h1 className="text-3xl font-black tracking-tighter uppercase">Detection Chronicle</h1>
              <p className="text-sm text-blue-100/40 mt-1 font-medium tracking-wide">Synthesized archive of all cognitive node detections and protocol triggers.</p>
           </div>
           <Button onClick={exportArchive} className="bg-emerald-600 hover:bg-emerald-500 text-white font-black px-8 py-6 rounded-2xl shadow-[0_0_30px_rgba(16,185,129,0.3)]">
              <Download className="w-4 h-4 mr-2" /> Full Archive Export
           </Button>
        </div>

        {/* Aggregate Insight */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
           <StatCard label="Total Events" value={events.length.toString()} icon={HistoryIcon} color="text-blue-400" bgColor="bg-blue-500/10" />
           <StatCard label="Visual Evidence" value={`${visualEvidencePct}%`} icon={Play} color="text-emerald-400" bgColor="bg-emerald-500/10" />
           <StatCard label="Anomalies" value={anomalyCount.toString()} icon={AlertTriangle} color="text-red-400" bgColor="bg-red-500/10" />
           <StatCard label="Avg Confidence" value={`${avgConfidence}%`} icon={Target} color="text-blue-400" bgColor="bg-blue-500/10" />
        </div>

        {/* Global Navigation */}
        <div className="flex bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-1 w-fit shadow-2xl">
           {[
             { id: 'log', label: 'Chronicle', icon: HistoryIcon },
             { id: 'analytics', label: 'Neural Insights', icon: BarChart3 }
           ].map(tab => (
             <button
               key={tab.id}
               onClick={() => setActiveTab(tab.id)}
               className={`flex items-center gap-2 px-8 py-2.5 rounded-xl font-bold text-xs uppercase tracking-widest transition-all ${activeTab === tab.id ? 'bg-blue-600 text-white shadow-[0_0_20px_rgba(37,99,235,0.4)]' : 'text-blue-100/40 hover:text-white'}`}
             >
               <tab.icon className="w-4 h-4" /> {tab.label}
             </button>
           ))}
        </div>

        {activeTab === 'log' ? (
          <div className="space-y-6">
             {/* Tactical Filters */}
             <GlassPanel>
                <div className="flex flex-col lg:flex-row gap-6">
                   <div className="relative flex-1">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-100/20" />
                      <Input 
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        placeholder="Search chronicle database..." 
                        className="bg-white/5 border-white/10 rounded-2xl py-6 pl-12 pr-6 text-white placeholder:text-blue-100/10" 
                      />
                   </div>
                   <div className="flex flex-wrap gap-4">
                      <Select value={filterType} onValueChange={setFilterType}>
                         <SelectTrigger className="w-44 bg-white/5 border-white/10 rounded-2xl h-full py-3 text-white uppercase text-[10px] font-black tracking-widest">
                            <SelectValue placeholder="EVENT TYPE" />
                         </SelectTrigger>
                         <SelectContent className="bg-[#0f111a] border-white/10 text-white">
                            <SelectItem value="all">All Types</SelectItem>
                            <SelectItem value="motion">A-Motion</SelectItem>
                            <SelectItem value="fire">Fire/Smoke</SelectItem>
                            <SelectItem value="fall">Patient Fall</SelectItem>
                            <SelectItem value="anpr">ANPR Recon</SelectItem>
                         </SelectContent>
                      </Select>

                      <Select value={filterSeverity} onValueChange={setFilterSeverity}>
                         <SelectTrigger className="w-44 bg-white/5 border-white/10 rounded-2xl h-full py-3 text-white uppercase text-[10px] font-black tracking-widest">
                            <SelectValue placeholder="SEVERITY" />
                         </SelectTrigger>
                         <SelectContent className="bg-[#0f111a] border-white/10 text-white">
                            <SelectItem value="all">Global Range</SelectItem>
                            <SelectItem value="high">Critical</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="low">Low</SelectItem>
                         </SelectContent>
                      </Select>
                      
                      <div className="bg-white/5 border border-white/10 rounded-2xl px-4 flex items-center">
                         <Calendar className="w-4 h-4 text-blue-400/40 mr-3" />
                         <span className="text-[10px] font-black text-blue-100/40 uppercase tracking-widest">Select Epoch</span>
                      </div>
                   </div>
                </div>
             </GlassPanel>

             {/* Chronicle Grid */}
             <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {filteredEvents.map((event) => (
                  <EventCard key={event.id} event={event} onClick={() => setSelectedEvent(event)} />
                ))}
                {filteredEvents.length === 0 && (
                   <div className="col-span-full py-40 text-center border-2 border-dashed border-white/5 rounded-[40px] bg-white/[0.01]">
                      <Search className="w-16 h-16 text-blue-500/10 mx-auto mb-6" />
                      <p className="text-blue-100/20 text-sm font-light italic uppercase tracking-widest">No intelligence found for the current query parameters.</p>
                   </div>
                )}
             </div>

             {/* Tactical Pagination */}
             <div className="flex items-center justify-between px-2 pt-8">
                <div className="text-[10px] font-black text-blue-100/20 uppercase tracking-widest">Showing {filteredEvents.length} Entries // Matrix Synchronized</div>
                <div className="flex gap-2">
                   <Button variant="outline" className="border-white/5 bg-white/5 text-blue-100/40 rounded-xl hover:text-white"><ChevronLeft className="w-4 h-4" /></Button>
                   <Button variant="outline" className="border-white/5 bg-white/5 text-blue-100/40 rounded-xl hover:text-white"><ChevronRight className="w-4 h-4" /></Button>
                </div>
             </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
             <div className="lg:col-span-2 space-y-8">
                <GlassPanel title="Detection Pulse" icon={Activity}>
                   <div className="h-64 flex items-end gap-2 px-2 pb-2">
                      {hourlyBuckets.map((b, i) => (
                         <div key={i} className="flex-1 flex flex-col gap-1 items-center group">
                            <div
                              className="w-full bg-blue-500/20 rounded-t-lg group-hover:bg-blue-500/40 transition-all cursor-pointer relative overflow-hidden"
                              style={{ height: `${b.count ? Math.max(8, (b.count / maxHourlyCount) * 100) : 2}%` }}
                            >
                               <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-blue-500/40 to-transparent" />
                            </div>
                            <span className="text-[8px] font-black text-blue-100/20 uppercase">{b.label}</span>
                         </div>
                      ))}
                      {events.length === 0 && (
                        <div className="absolute inset-0 flex items-center justify-center text-[10px] text-blue-100/20 uppercase tracking-widest">No events in range</div>
                      )}
                   </div>
                </GlassPanel>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                   <GlassPanel title="Location Load" icon={Layers}>
                      <div className="space-y-6">
                         {topLocations.length === 0 && (
                           <p className="text-[10px] text-blue-100/20 uppercase tracking-widest">No location data yet.</p>
                         )}
                         {topLocations.map(([location, count], idx) => (
                           <div key={idx} className="space-y-2">
                              <div className="flex justify-between text-[10px] font-black uppercase tracking-widest">
                                 <span className="text-blue-100/40">{location}</span>
                                 <span className="text-white">{count} events</span>
                              </div>
                              <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
                                 <div className="h-full bg-blue-500 shadow-[0_0_10px_rgba(0,0,0,0.5)]" style={{ width: `${(count / maxLocationCount) * 100}%` }} />
                              </div>
                           </div>
                         ))}
                      </div>
                   </GlassPanel>

                   <GlassPanel title="Avg Detection Confidence" icon={Zap}>
                      <div className="flex items-center justify-center py-8">
                         <div className="relative w-40 h-40">
                            <svg className="w-full h-full transform -rotate-90">
                               <circle cx="80" cy="80" r="70" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-white/5" />
                               <circle cx="80" cy="80" r="70" stroke="currentColor" strokeWidth="8" fill="transparent" strokeDasharray="440" strokeDashoffset={440 - (440 * (avgConfidence / 100))} className="text-blue-500" strokeLinecap="round" />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                               <span className="text-3xl font-black text-white">{avgConfidence}</span>
                               <span className="text-[9px] font-black text-blue-400 uppercase tracking-widest">Confidence</span>
                            </div>
                         </div>
                      </div>
                   </GlassPanel>
                </div>
             </div>

             <div className="space-y-8">
                <GlassPanel title="Detection Category Distribution" icon={Filter}>
                   <div className="space-y-6">
                      {Object.entries(typeLabels).map(([key, meta], idx) => (
                        <div key={idx} className="flex items-center justify-between group">
                           <div className="flex items-center gap-3">
                              <meta.icon className={`w-4 h-4 ${meta.color}`} />
                              <span className="text-xs font-bold text-white uppercase group-hover:text-blue-400 transition-colors">{meta.label}</span>
                           </div>
                           <span className="text-xs font-mono text-blue-100/40">{typeCounts[key] || 0} detections</span>
                        </div>
                      ))}
                   </div>
                </GlassPanel>

                <GlassPanel className="bg-blue-600/10 border-blue-500/20">
                   <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <Activity className="w-4 h-4 text-blue-400" />
                        <h4 className="text-xs font-black text-white uppercase">Anomalies Detected</h4>
                      </div>
                      <p className="text-[10px] text-blue-100/60 leading-relaxed font-light italic">
                        {anomalyCount > 0
                          ? `${anomalyCount} high-severity event${anomalyCount === 1 ? '' : 's'} detected across ${topLocations.length} location${topLocations.length === 1 ? '' : 's'} in the loaded window.`
                          : 'No high-severity anomalies in the loaded window.'}
                      </p>
                      <Button
                        onClick={() => { setFilterSeverity('high'); setActiveTab('log'); }}
                        className="w-full bg-blue-500/20 hover:bg-blue-500/40 text-blue-400 border border-blue-500/30 font-black text-[10px] uppercase py-3 rounded-xl transition-all"
                      >
                        View High-Severity Events
                      </Button>
                   </div>
                </GlassPanel>
             </div>
          </div>
        )}
      </div>
    </div>
  );
}